<script setup lang="ts">
import { ElDatePicker } from 'element-plus'
import { type Component, ref } from 'vue'

import CalendarIcon from '../../assets/icons/calendar.svg?component'

import BoxComponent from './BoxComponent.vue'
import TypographyComponent from './TypographyComponent.vue'

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'auto'
  | 'auto-start'
  | 'auto-end'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'right-start'
  | 'right-end'
  | 'left-start'
  | 'left-end'

const props = withDefaults(
  defineProps<{
    title?: string
    defaultValue?: Date
    placeholder?: string
    inputNote?: string
    prefixIcon?: Component
    isDisabled?: boolean
    disabledDate: (date: Date) => boolean
    name?: string
    format?: string
    dateFormat?: string
    timeFormat?: string
    valueFormat?: string
    size?: 'default' | 'small' | 'large'
    type?:
      | 'year'
      | 'years'
      | 'month'
      | 'months'
      | 'date'
      | 'dates'
      | 'datetime'
      | 'week'
      | 'datetimerange'
      | 'daterange'
      | 'monthrange'
      | 'yearrange'
    shortcuts?: { text: string; value: string[] }[]
    popperOptions?: {
      placement: Placement
      modifiers?: { name: string; options?: Record<string, unknown> }[]
    }
    popperClass?: string
    defaultTime?: Date
    teleported?: boolean
    showConfirm?: boolean
  }>(),
  {
    title: undefined,
    isDisabled: false,
    placeholder: 'Select',
    prefixIcon: CalendarIcon,
    inputNote: '',
    defaultValue: () => new Date(),
    defaultTime: undefined,
    disabledDate: () => false,
    type: 'datetime',
    name: undefined,
    format: undefined,
    dateFormat: undefined,
    timeFormat: undefined,
    valueFormat: undefined,
    size: 'default',
    teleported: true,
    showConfirm: true,
  },
)

const emit = defineEmits<{ 'calendar-change': [dates: [Date, Date | null]] }>()

const value = defineModel<string | [string, string] | Date>('modelValue', { default: '' })
const datePickerRef = ref<typeof ElDatePicker>()
const open = () => datePickerRef.value?.handleOpen()
const close = () => datePickerRef.value?.handleClose()

const onSelectedDateUpdate = (val: unknown) => {
  if (!props.defaultTime) {
    return
  }

  if (props.type?.includes('range')) {
    // Only single datepickers without ranges are supported at the moment
    return
  }

  // element-plus datepicker does support default-time prop, but it only works with Date objects.
  // so instead, we need to manually set the time
  const dateValue = val instanceof Date ? val : new Date(val as string)
  const defaultTime = props.defaultTime
  if (dateValue.toTimeString().startsWith('00:00:00')) {
    dateValue.setHours(defaultTime.getHours(), defaultTime.getMinutes(), defaultTime.getSeconds())
  }
  value.value = typeof value.value === 'string' ? dateValue.toISOString() : dateValue
}

defineExpose({
  open,
  close,
})
</script>
<template>
  <BoxComponent>
    <BoxComponent v-if="title" :mb="8">
      <TypographyComponent variant="title-large">{{ title }}</TypographyComponent>
    </BoxComponent>
    <ElDatePicker
      ref="datePickerRef"
      v-model="value"
      :name="name"
      :disabled="isDisabled"
      :type="type"
      :format="format"
      :date-format="dateFormat"
      :time-format="timeFormat"
      :value-format="valueFormat"
      :size="size"
      :popper-options="popperOptions"
      :popper-class="popperClass"
      :placeholder="placeholder"
      :prefix-icon="prefixIcon"
      :default-value="defaultValue"
      :disabled-date="disabledDate"
      :show-confirm="showConfirm"
      :shortcuts="shortcuts"
      :teleported="teleported"
      @update:model-value="onSelectedDateUpdate"
      @calendar-change="emit('calendar-change', $event)"
    />
    <TypographyComponent v-if="inputNote" variant="body-small">{{ inputNote }}</TypographyComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
:deep(.el-input__wrapper) {
  padding: 16px;
  line-height: 24px;
  border-radius: 8px;
  border: none;
  background: var(--inverted);
  box-shadow: 0 0 0 1px var(--secondary-12);

  &:hover,
  &.is-focused {
    box-shadow: 0 0 0 1px var(--secondary-64);
  }
}
:deep(.el-date-editor) {
  --el-date-editor-width: 100%;
  --el-input-height: 56px;

  .el-icon {
    width: 24px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
